<template>
  <v-dialog v-model="dialog" persistent max-width="800px">
    <template #activator="{ on }">
      <slot :on="on"></slot>
    </template>
    <v-card class="p-5 p-md-8">
      <ModalHeading
        :modalTitle="
          formMode === 'add' ? 'Add Certificate' : 'Edit Certificate'
        "
        modalDesc="Fill in your certificates data to highlight your expertise"
      />
      <form class="form pt-3" @submit.prevent="submitForm">
        <div class="row">
          <div class="col-md-6 mb-7">
            <label class="form__label">
              Certificate Name <span class="text-danger">*</span></label
            >
            <v-text-field
              outlined
              dense
              hide-details
              placeholder="Certificate Name"
              v-model="certification.name"
              @blur="$v.certification.name.$touch()"
            >
            </v-text-field>
            <VulidateError
              :validationField="$v.certification.name"
              :params="[
                'required',
                'alphaNumSpace',
                {
                  maxLength: {
                    number: $v.certification.name.$params.maxLength.max,
                  },
                },
              ]"
            />
          </div>
          <div class="col-md-6 mb-7">
            <label class="form__label"> Issuing Organization</label>
            <v-text-field
              outlined
              dense
              hide-details
              placeholder="Issuing Organization"
              v-model="certification.organization"
              @blur="$v.certification.organization.$touch()"
            >
            </v-text-field>
            <VulidateError
              :validationField="$v.certification.organization"
              :params="[
                'alphaNumSpace',
                {
                  maxLength: {
                    number: $v.certification.organization.$params.maxLength.max,
                  },
                },
              ]"
            />
          </div>
          <div class="col-12 mb-7 v-checkbox d-flex">
            <v-checkbox
              v-model="certification.not_expire"
              :false-value="0"
              :true-value="1"
              label="This credential does not expire"
              hide-details
              @change="checkEndDate"
            ></v-checkbox>
          </div>
          <div class="col-md-6 mb-7">
            <label class="form__label">Issuing date</label>
            <div class="row">
              <div class="col-6">
                <v-select
                  :items="MONTHS"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  placeholder="Select Month"
                  v-model="certification.start_month"
                  :menu-props="{ bottom: true }"
                ></v-select>
              </div>
              <div class="col-6">
                <v-select
                  :items="YEARS"
                  outlined
                  dense
                  hide-details
                  placeholder="Select Year"
                  v-model="certification.start_year"
                  :menu-props="{ bottom: true }"
                ></v-select>
              </div>
            </div>
            <div
              class="form__error-label"
              v-if="
                certification.start_month &&
                certification.start_year &&
                $v.$anyDirty &&
                $v.validStartDate.$error
              "
            >
              Please Enter a Valid Date
            </div>
            <div
              class="form__error-label"
              v-if="
                certification.start_month &&
                certification.start_year &&
                certification.end_month &&
                certification.end_year &&
                $v.$anyDirty &&
                $v.validEndDate.$error
              "
            >
              Issuing date must be before Expiration date
            </div>
          </div>

          <div class="col-md-6 mb-7">
            <label class="form__label">Expiration date</label>
            <div class="row">
              <div class="col-6">
                <v-select
                  :items="MONTHS"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  placeholder="Select Month"
                  v-model="certification.end_month"
                  @blur="$v.certification.end_month.$touch()"
                  :menu-props="{ bottom: true }"
                  :disabled="!!certification.not_expire"
                ></v-select>
                <VulidateError
                  :validationField="$v.certification.end_month"
                  :params="['required']"
                />
              </div>
              <div class="col-6">
                <v-select
                  :items="YEARS"
                  outlined
                  dense
                  hide-details
                  placeholder="Select Year"
                  v-model="certification.end_year"
                  @blur="$v.certification.end_year.$touch()"
                  :menu-props="{ bottom: true }"
                  :disabled="!!certification.not_expire"
                ></v-select>
                <VulidateError
                  :validationField="$v.certification.end_year"
                  :params="['required']"
                />
              </div>
            </div>
          </div>
          <div class="col-12 mb-7">
            <label class="form__label"> Credential ID</label>
            <v-text-field
              outlined
              dense
              hide-details
              placeholder="Credential ID"
              v-model="certification.identifier"
              @blur="$v.certification.identifier.$touch()"
            >
            </v-text-field>
            <VulidateError
              :validationField="$v.certification.identifier"
              :params="[
                {
                  maxLength: {
                    number: $v.certification.identifier.$params.maxLength.max,
                  },
                },
              ]"
            />
          </div>
          <div class="col-12 mb-7">
            <label class="form__label"> Credential URL</label>
            <v-text-field
              outlined
              dense
              hide-details
              placeholder="Credential URL"
              v-model="certification.url"
              @blur="$v.certification.url.$touch()"
            >
            </v-text-field>
            <VulidateError
              :validationField="$v.certification.url"
              :params="[
                'urlFormat',
                {
                  maxLength: {
                    number: $v.certification.url.$params.maxLength.max,
                  },
                },
              ]"
            />
          </div>
        </div>
        <div class="text-right pt-2">
          <button
            class="btn btn-secondary font-weight-bolder font-size-h6 px-8"
            type="button"
            @click="resetModal"
          >
            Cancel
          </button>
          <button
            class="btn btn-info font-weight-bolder font-size-h6 px-8 ml-5"
            :class="{
              'spinner spinner-light spinner-right': loading,
            }"
            :disabled="loading"
          >
            Save
          </button>
        </div>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import {
  required,
  requiredIf,
  maxLength,
  helpers,
  sameAs,
} from "vuelidate/lib/validators";
import ModalHeading from "@/components/UI/ModalHeading.vue";
import VulidateError from "@/components/shared/vulidateError.vue";
import MONTHS from "@/constants/months";
import YEARS from "@/constants/years";
import { urlFormat, lessThanOrEqualCurrentDate, validDate } from "@/helpers";
const alphaNumSpace = helpers.regex("alphaNumSpace", /^[A-Za-z0-9 ]+$/);

const initCertificationObj = {
  name: "",
  organization: "",
  not_expire: 0,
  identifier: "",
  url: "",
  start_year: null,
  start_month: null,
  end_year: null,
  end_month: null,
};
export default {
  name: "CertificationFormModal",
  mixins: [validationMixin],
  components: {
    ModalHeading,
    VulidateError,
  },
  props: {
    activeCertification: {
      required: true,
    },
    certificatesHasAnEmptyRow: {
      type: Array,
    },
  },
  data() {
    return {
      dialog: false,
      formMode: "add",
      certification: { ...initCertificationObj },
      MONTHS,
      YEARS,
    };
  },
  watch: {
    activeCertification(newVal) {
      if (newVal) {
        this.formMode = "edit";
        this.certification = { ...newVal };
        if (
          this.certificatesHasAnEmptyRow &&
          this.certificatesHasAnEmptyRow.find((e) => e.id === newVal.id)
        ) {
          setTimeout(() => {
            this.$v.$touch();
          }, 100);
        }
      } else {
        this.formMode = "add";
        this.certification = { ...initCertificationObj };
      }
    },
  },
  validations: {
    certification: {
      name: {
        required,
        maxLength: maxLength(100),
        alphaNumSpace,
      },
      organization: {
        maxLength: maxLength(100),
        alphaNumSpace,
      },
      end_month: {
        required: requiredIf(function () {
          return (
            !this.certification.not_expire &&
            this.certification.start_month &&
            this.certification.start_year
          );
        }),
      },
      end_year: {
        required: requiredIf(function () {
          return (
            !this.certification.not_expire &&
            this.certification.start_month &&
            this.certification.start_year
          );
        }),
      },
      identifier: {
        maxLength: maxLength(100),
      },
      url: {
        urlFormat,
        maxLength: maxLength(2000),
      },
    },
    validStartDate: {
      sameAs: sameAs(() => true),
    },
    validEndDate: {
      sameAs: sameAs(() => true),
    },
  },
  computed: {
    ...mapGetters({
      loading: "getLoadingStatus",
    }),
    validStartDate() {
      return (
        (!this.certification.start_month && !this.certification.start_year) ||
        (this.certification.start_month &&
          this.certification.start_year &&
          lessThanOrEqualCurrentDate(
            this.certification.start_month,
            this.certification.start_year
          ))
      );
    },
    validEndDate() {
      if (
        (!this.certification.start_month && !this.certification.start_year) ||
        this.certification.not_expire
      )
        return true;
      return (
        this.certification.start_month &&
        this.certification.start_year &&
        this.certification.end_month &&
        this.certification.end_year &&
        validDate(
          this.certification.start_month,
          this.certification.start_year,
          this.certification.end_month,
          this.certification.end_year
        )
      );
    },
  },
  methods: {
    ...mapActions({
      addTalentCertification: "talentProfile/addTalentCertification",
      editTalentCertification: "talentProfile/editTalentCertification",
    }),
    checkEndDate(value) {
      if (value) {
        this.certification.end_month = "";
        this.certification.end_year = "";
        this.$v.certification.end_month.$reset();
        this.$v.certification.end_year.$reset();
      }
    },
    resetModal() {
      this.dialog = false;
      this.$v.$reset();
      this.certification = { ...initCertificationObj };
    },
    submitForm() {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }

      let payload = {
        data: this.certification,
        talent_id: this.$route.params.id,
      };
      this.$emit("editCertification", payload.data.name);
      if (this.formMode === "add") {
        this.addTalentCertification(payload).then(() => {
          this.resetModal();
        });
      } else {
        if (!this.activeCertification.id) {
          payload.edit = true;
          this.addTalentCertification(payload).then(() => {
            this.resetModal();
            this.$emit("getImportedCv");
          });
        } else {
          this.editTalentCertification(payload).then(() => {
            this.resetModal();
            this.$emit("getImportedCv");
          });
        }
      }
    },
  },
};
</script>
